import { approximateDistance, formatNumberWithSpaces, getMeasurementSystem } from "../utils/Utils";
import { getLibMeasurementSystem, haversineDistance } from "../utils/Utils";
import { Coordinates } from "./Coordinates";
import { Pleb } from "./Pleb";

interface EventData {
  id: string;
  address: string;
  description: string;
  startDate: string;
  endDate: string;
  pictures: string[];
  coordinates?: Coordinates;
  name: string;
  image: string;
  thumbnail: string;
  venue: string | null;
  timeZone: string;
  organizer: Pleb | null;
}
  
export enum FormatDate{
    dateHour = 'dateHour',
    date = 'date',
    hour = 'hour',
}


export class Event {
  id: string;
  address: string;
  description: string;
  startDate: string;
  endDate: string;
  pictures: string[];
  coordinates?: Coordinates;
  name: string;
  image: string;
  thumbnail: string;
  venue: string | null;
  timeZone: string;
  organizer: Pleb | null;

  constructor(data: EventData) {
    this.id = data.id;
    this.name = data.name;
    this.description = data.description;
    this.address = data.address;
    this.startDate = data.startDate;
    this.endDate = data.endDate;
    this.pictures = data.pictures;
    this.coordinates = data.coordinates;
    this.image = data.image;
    this.thumbnail = data.thumbnail;
    this.venue = data.venue;
    this.timeZone = data.timeZone;
    this.organizer = data.organizer;
  }

  getDateFormatted(format: FormatDate): string {
    const startDate = new Date(this.startDate);
    const endDate = new Date(this.endDate);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    let startHour : string | null = startDate.toLocaleTimeString('en-US', { 
        hour: 'numeric',
        minute: '2-digit',
        hour12: false,
        timeZone: this.timeZone
    });

    let endHour : string | null = endDate.toLocaleTimeString('en-US', { 
        hour: 'numeric',
        minute: '2-digit',
        hour12: false,
        timeZone: this.timeZone
    });

    const todayDateString = today.toLocaleDateString('en-US', { 
        month: 'short',
        day: 'numeric',
        timeZone: this.timeZone
    });

    const tomorrowDateString = tomorrow.toLocaleDateString('en-US', { 
        month: 'short',
        day: 'numeric',
        timeZone: this.timeZone
    });

    const startDateString = startDate.toLocaleDateString('en-US', { 
        month: 'short',
        day: 'numeric',
        timeZone: this.timeZone
    });

    const endDateString = endDate.toLocaleDateString('en-US', { 
        month: 'short',
        day: 'numeric',
        timeZone: this.timeZone
    });

    // Get date part based on event duration
    let datePart = '';
    if (startDateString !== endDateString) {
        // Multi-day event
        if (startDate.getMonth() === endDate.getMonth()) {
            datePart = `${startDate.toLocaleDateString('en-US', { 
                month: 'short',
                day: 'numeric',
                timeZone: this.timeZone
            })} - ${endDate.toLocaleDateString('en-US', {
                day: 'numeric',
                timeZone: this.timeZone
            })}`;
        } else {
            datePart = `${startDate.toLocaleDateString('en-US', { 
                month: 'short',
                day: 'numeric',
                timeZone: this.timeZone
            })} - ${endDate.toLocaleDateString('en-US', { 
                month: 'short',
                day: 'numeric',
                timeZone: this.timeZone
            })}`;
        }

        startHour = null;
    } else {
        // Single day event
        if (startDateString === todayDateString) {
            datePart = 'Today';
        } else if (startDateString === tomorrowDateString) {
            datePart = 'Tomorrow';
        } else {
            datePart = startDate.toLocaleDateString('en-US', { 
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                timeZone: this.timeZone
            });
        }
    }

    switch (format) {
        case FormatDate.date:
            return datePart;
        case FormatDate.hour:
            return (startHour && endHour) ? `${startHour} - ${endHour}` : '';
        case FormatDate.dateHour:
            return startHour ? `${datePart} - ${startHour}` : datePart;
    }
  }

  static fromJson(json: Record<string, any>, currentUserId: string): Event {
    const coordinatesList = json.location?.coordinates as number[] | undefined;
    let coordinates: Coordinates | undefined;
    if (coordinatesList?.length) {
      coordinates = {
        latitude: coordinatesList[1],
        longitude: coordinatesList[0]
      };
    }

    let organizer: Pleb | null = null;
    if(json.createdBy && json.createdBy.id) {
        organizer = Pleb.fromJson({user: json.createdBy}, currentUserId);
    }

    const event = new Event({
        id: json._id,
        name: json.name,
        description: json.description,
        address: json.address,
        startDate: json.startDate,
        endDate: json.endDate,
        pictures: json.pictures.map((p: any) => p.url),
        image: json.publicPictureURL,
        thumbnail: json.publicThumbnailURL ?? json.publicPictureURL,
        coordinates: coordinates,
        venue: json.venue,
        timeZone: json.timeZone,
        organizer: organizer,
    });
    return event;
  }
}


export function getDistanceBetweenCoordinateAndEvent(coordinates: Coordinates, event: Event) : string | null {
    if(!coordinates || !event.coordinates) {
      return null;
    }
  
    const distanceInM = haversineDistance(coordinates.latitude, coordinates.longitude, event.coordinates.latitude, event.coordinates.longitude);
  
    return formatNumberWithSpaces(approximateDistance(distanceInM, getMeasurementSystem(), true)) + ' ' + getLibMeasurementSystem();
}